import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { zip } from 'rxjs';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { Project } from '../../classes/project.class';
import { Realisation, RealisationHourtype } from '../../classes/realisation';
import { User } from '../../classes/user.class';
import { CodaltComponent } from '../../codalt.component';
import { ApiResponse } from '../../services/api/api.service';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { RealisationService } from '../../services/realisation.service';
import { LocalStorage } from '../../storage.class';
import { Utils } from '../../utils.class';

@Component({
    selector: 'app-copy-staff-from-other-day',
    templateUrl: './copy-staff-from-other-day.component.html',
    styleUrls: ['./copy-staff-from-other-day.component.scss'],
    standalone: false,
})
export class CopyStaffFromOtherDayComponent extends CodaltComponent implements OnInit {
    fcUsers = new FormControl<User[]>([]);
    fcHourtype = new FormControl<RealisationHourtype>(RealisationHourtype.worktime);

    staffList: User[] | null = null;
    hadUsersOnPreviousDay = false;
    allExcludedBecauseAlreadyHours = false;

    RealisationHourtype = RealisationHourtype;

    hourtypeOptions: RealisationHourtype[] = [
        RealisationHourtype.worktime,
        RealisationHourtype.sleep,
        RealisationHourtype.bank_holiday,
        RealisationHourtype.frost,
        RealisationHourtype.day_off,
        RealisationHourtype.illness,
        RealisationHourtype.education,
        RealisationHourtype.short_absence,
        RealisationHourtype.paid_leave,
        RealisationHourtype.time_for_time,
        RealisationHourtype.special_leave,
    ];

    constructor(
        public dialogRef: MatDialogRef<CopyStaffFromOtherDayComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            project: Project;
            date: Date;
            realisations: Realisation[];
        },
        private realisationService: RealisationService
    ) {
        super();
    }

    ngOnInit(): void {
        const prevDate = new Date(this.data.date);
        prevDate.setDate(prevDate.getDate() - 1);

        this.subscriptions.add(
            this.realisationService.getDaySchedule(prevDate).subscribe((response: ApiResponse<Realisation[]>) => {
                const prevRealisations = response.data;

                const currentUserIds = new Set(
                    this.data.realisations.filter(r => !!r.user_id && !r.removed).map(r => r.user_id)
                );

                const seenUserIds = new Set<number>();
                const users: User[] = [];
                const previousDayProjectUsers = new Set<number>();

                prevRealisations
                    .filter(r => {
                        const sameProject = Utils.realisationAfasProjectId(r) === this.data.project.afas_project_id;

                        if (sameProject && !!r.user_id) {
                            previousDayProjectUsers.add(r.user_id);
                        }

                        return sameProject && !!r.user_id && !currentUserIds.has(r.user_id) && !!r.user;
                    })
                    .forEach(r => {
                        if (!seenUserIds.has(r.user_id)) {
                            seenUserIds.add(r.user_id);
                            users.push(r.user);
                        }
                    });

                this.hadUsersOnPreviousDay = previousDayProjectUsers.size > 0;
                this.allExcludedBecauseAlreadyHours = this.hadUsersOnPreviousDay && users.length === 0;

                if (AuthorisationService.hasFunctionality(Functionalities.HourCheckRestricted)) {
                    const myId = LocalStorage.user.id;
                    const myRoles = LocalStorage.user.roles.map(r => r.key);
                    const canAddUser = (u: User) =>
                        u.id === myId || u.hours_checked_by === myId || myRoles.includes(u.hours_checked_by_role);
                    this.staffList = users.filter(u => canAddUser(u));
                } else {
                    this.staffList = users;
                }

                this.fcUsers.setValue([...this.staffList]);
            })
        );
    }

    take() {
        const selectedUsers: User[] = this.fcUsers.value || [];
        if (!selectedUsers.length) {
            this.dialogRef.close();
            return;
        }

        const saveCalls$ = selectedUsers.map(user => {
            const realisation = new Realisation();
            realisation.user_id = user.id;
            realisation.afas_project_id = this.data.project.afas_project_id;
            realisation.hourtype = this.fcHourtype.value;
            realisation.bookdate = this.data.date;
            realisation.begindate = Utils.setTimeNewDate(this.data.date, 7, 0);
            realisation.enddate = Utils.addMinutes(realisation.begindate, 60 * 8);
            return this.realisationService.saveRealisation(realisation);
        });

        this.subscriptions.add(
            zip(...saveCalls$).subscribe((realisations: ApiResponse<{ realisation: Realisation }>[]) => {
                this.dialogRef.close(realisations[0].data.realisation);
            })
        );
    }
}
